import {
  DEFAULT_CALENDAR_COLOURS,
  DEFAULT_GLOW,
  mergeOrgTheme,
  resolveCalendarColours,
  resolveGlow,
  resolveThemeDefaults,
  themeVarName,
} from '@mfw/common';
import { createContextFactory } from '@mfw/components';
import React from 'react';

import type { CalendarColourMode, GlowIntensity } from '@mfw/common';
import type { Maybe } from '../types/maybe.types';

import { readBrandingCache, writeBrandingCache } from '../theme/brandingCache';
import { writeResolvedTheme } from '../theme/resolvedTheme';

import { useTenantContext } from './TenantProvider';

/**
 * Example:
 * --mfw-palette-action-disabled: rgba(145, 158, 171, 0.8);
 * --mfw-palette-action-disabledBackground: rgba(145, 158, 171, 0.24);
 * --mfw-palette-action-disabledOpacity: 0.48;
 * --mfw-palette-action-focus: rgba(145, 158, 171, 0.24);
 * --mfw-palette-action-hover: rgba(145, 158, 171, 0.08);
 * --mfw-palette-action-hoverOpacity: 0.08;
 * --mfw-palette-action-selected: rgba(145, 158, 171, 0.16);
 * --mfw-palette-action-active: #919EAB;
 * --mfw-palette-action-selectedOpacity: 0.16;
 * --mfw-palette-action-focusOpacity: 0.12;
 * --mfw-palette-action-activatedOpacity: 0.24;
 * --mfw-palette-action-activeChannel: 145 158 171;
 * --mfw-palette-action-selectedChannel: 145 158 171;
 */
type ThemeActionColors = {
  '--mfw-palette-action-disabledBackground': string;
  '--mfw-palette-action-activatedOpacity': string;
  '--mfw-palette-action-disabledOpacity': string;
  '--mfw-palette-action-selectedOpacity': string;
  '--mfw-palette-action-selectedChannel': string;
  '--mfw-palette-action-activeChannel': string;
  '--mfw-palette-action-hoverOpacity': string;
  '--mfw-palette-action-focusOpacity': string;
  '--mfw-palette-action-disabled': string;
  '--mfw-palette-action-selected': string;
  '--mfw-palette-action-active': string;
  '--mfw-palette-action-focus': string;
  '--mfw-palette-action-hover': string;
};

type ThemeCommonColors = {
  '--mfw-palette-common-onBackgroundChannel': string;
  '--mfw-palette-common-onDark': string;
  '--mfw-palette-common-onDarkChannel': string;
  '--mfw-palette-transparent': string;
};
/**
 * The second brand anchor and its derived ramp — the gradient's far stop.
 */
type ThemeAccentColors = {
  '--mfw-palette-accent-contrastText': string;
  '--mfw-palette-accent-mainChannel': string;
  '--mfw-palette-accent-light': string;
  '--mfw-palette-accent-main': string;
  '--mfw-palette-accent-dark': string;
};
/**
 * Very low-chroma washes of the brand/accent hue, for the large tinted fields
 * behind hero and section backgrounds.
 */
type ThemeAmbientColors = {
  '--mfw-palette-ambient-accent': string;
  '--mfw-palette-ambient-brand': string;
};
type ThemeTextColors = {
  '--mfw-palette-text-primary-mainChannel': string;
  '--mfw-palette-text-secondaryChannel': string;
  '--mfw-palette-text-secondary': string;
  '--mfw-palette-text-disabled': string;
  '--mfw-palette-text-primary': string;
  '--mfw-palette-text-icon': string;
};
type ThemeBackgroundColors = {
  '--mfw-palette-background-default': string;
  /**
   * For example, table header background
   */
  '--mfw-palette-background-neutral': string;
  /**
   * Color used for dimming background for images such as FeatureCarousel component, number indicates at what percentage to dim
   */
  '--mfw-palette-background-dim-100': string;
  /**
   * Colors used for gradient of the table 0% and 100%.
   */
  '--mfw-palette-background-table-0': string;
  '--mfw-palette-background-table-1': string;

  '--mfw-palette-background-dim-10': string;
  '--mfw-palette-background-paper': string;
  /**
   * 1px separators — a toned step of the brand hue, not a grey, so it stays
   * part of the surface ladder after a rebrand. Catalog group: `background`.
   */
  '--mfw-palette-border-hairline': string;
};

type ThemeErrorColors = {
  '--mfw-palette-error-contrastText': string;
  '--mfw-palette-error-lighter': string;
  '--mfw-palette-error-darker': string;
  '--mfw-palette-error-light': string;
  '--mfw-palette-error-main': string;
  '--mfw-palette-error-dark': string;
};

type ThemeSuccessColors = {
  '--mfw-palette-success-contrastText': string;
  '--mfw-palette-success-lighter': string;
  '--mfw-palette-success-darker': string;
  '--mfw-palette-success-light': string;
  '--mfw-palette-success-main': string;
  '--mfw-palette-success-dark': string;
};

type ThemeWarningColors = {
  '--mfw-palette-warning-contrastText': string;
  '--mfw-palette-warning-lighter': string;
  '--mfw-palette-warning-darker': string;
  '--mfw-palette-warning-light': string;
  '--mfw-palette-warning-main': string;
  '--mfw-palette-warning-dark': string;
};

type ThemePrimaryColors = {
  '--mfw-palette-primary-contrastText': string;
  '--mfw-palette-primary-mainChannel': string;
  '--mfw-palette-primary-lighter': string;
  '--mfw-palette-primary-darker': string;
  '--mfw-palette-primary-light': string;
  '--mfw-palette-primary-dark': string;
  '--mfw-palette-primary-main': string;
};
type ThemeSecondaryColors = {
  '--mfw-palette-secondary-lighter': string;
  '--mfw-palette-secondary-darker': string;
  '--mfw-palette-secondary-light': string;
  '--mfw-palette-secondary-dark': string;
  '--mfw-palette-secondary-main': string;
};

type ThemeEventColors = {
  '--mfw-palette-event-completed': string;
  '--mfw-palette-event-canceled': string;
  '--mfw-palette-event-pending': string;
  '--mfw-palette-event-missed': string;
};

/**
 * Actual colors available for change in theme.
 *
 * This is what `useThemeVars()` is typed by, and per
 * `packages/@mfw-frontend/CLAUDE.md` `useThemeVars()[themeVarName.*]` is THE
 * way to read a theme colour where a `var()` reference cannot work (recharts /
 * SVG presentation attributes, colour maths). A catalog var missing here is
 * therefore applied to `:root` at runtime — `applyTheme` iterates the object,
 * not this type — but unreadable from TypeScript. `THEME_COLOR_VAR_NAMES`
 * below makes that a compile error rather than a discovery.
 *
 * It is deliberately a SUPERSET of the catalog: MUI writes a further set of
 * `--mfw-palette-action-*` and `--mfw-palette-text-*Channel` vars that
 * `THEME_VARS` does not curate.
 */
export type ThemeColors = ThemeBackgroundColors &
  ThemeSecondaryColors &
  ThemePrimaryColors &
  ThemeSuccessColors &
  ThemeWarningColors &
  ThemeAmbientColors &
  ThemeCommonColors &
  ThemeActionColors &
  ThemeAccentColors &
  ThemeErrorColors &
  ThemeEventColors &
  ThemeTextColors;

/**
 * Every `--mfw-palette-*` var the catalog curates, typed as `keyof
 * ThemeColors` — the drift guard for the type above, in both directions:
 *
 * - a var added to `THEME_VARS` and forgotten here fails `typecheck` on this
 *   annotation, because `themeVarName` is the catalog's literal-typed
 *   projection (`theme-vars.spec.ts` asserts that projection is 1:1 with
 *   `THEME_VARS` at runtime);
 * - `AppThemeProvider.spec.ts` asserts this list equals
 *   `resolveThemeDefaults()`'s key set, so the annotation cannot quietly stop
 *   covering the whole catalog.
 */
export const THEME_COLOR_VAR_NAMES: readonly (keyof ThemeColors)[] =
  Object.values(themeVarName).flatMap((group) => Object.values(group));

interface ThemeOptions {
  themeLayout: 'horizontal' | 'mini' | 'vertical';
  themeContrast: 'bold' | 'default';
  themeDirection: 'ltr' | 'rtl';
  themeStretch: boolean;
}

export type AppThemeProviderProps = {
  children: React.ReactNode;
};

type ThemeContext = {
  setThemeOptions: React.Dispatch<React.SetStateAction<ThemeOptions>>;
  /**
   * Rebrand: pushes the active org's raw theme overrides (the editable var
   * subset, verbatim) — or `undefined`/`null` to revert to the tenant theme.
   * The full merge (defaults ← tenant ← org, with derived recompute) is done
   * here in ONE place via `mergeOrgTheme`; the bridge in OrganizationProvider
   * only supplies the raw org theme so there is no second merge/derivation.
   */
  setOrgTheme: (orgTheme: Maybe<Record<string, string>>) => void;
  /**
   * The organization layer has settled: the session, `/auth/me` and the
   * switcher have answered, and the theme pushed through `setOrgTheme` is the
   * one this load will keep. Called by the bridge in OrganizationProvider with
   * the logo of the branding in effect (null = the tenant's own). Until this
   * AND the tenant have settled, the cached branding is what is painted.
   */
  settleOrgBranding: (logoUrl: Maybe<string>) => void;
  /**
   * The live branding has settled (or the wait timed out). Before that, the
   * page wears the cached branding, or — on a device with none — sits behind
   * `BrandingLoader`.
   */
  isBrandingReady: boolean;
  /** Whether this load opened in a cached branding. */
  hasCachedBranding: boolean;
  /** The cached logo, while the live one is not known yet. */
  pendingLogoUrl: Maybe<string>;
  /**
   * @returns Apply theme variables to global :root selector
   */
  applyTheme: () => void;
  /** Returns theme */
  theme: ThemeColors;
} & ThemeOptions;

const Context = createContextFactory({
  devToolsName: 'ThemeProvider',
  initialValue: {} as ThemeContext,
});

// Generated from THEME_VARS (@mfw/common) — the single source of truth for
// runtime CSS var defaults. See packages/@mfw-common/theme/themeVars.ts.
const initialTheme = resolveThemeDefaults() as ThemeColors;

// Will come from BE
const options = {
  themeContrast: 'default',
  themeDirection: 'ltr',
  themeLayout: 'horizontal',
  themeStretch: false,
} satisfies ThemeOptions;

/**
 * Writes a resolved theme onto an element: every `--mfw-palette-*` var, the
 * player's own colour, the surface flag, the calendar colour mode and the
 * measured aliases — through `writeResolvedTheme`, the writer the rebrand
 * editor's draft preview shares, so the two cannot paint one record
 * differently — plus the glow setting.
 *
 * The calendar mode defaults to Brand, so a caller with no org (the personal
 * profile, a platform user's workspace) RESETS whatever mode the last org
 * left on the root rather than inheriting it.
 *
 * Exported and element-taking (rather than reaching for `document` itself) so
 * the contract is assertable without mounting the provider tree.
 */
export function applyThemeToRoot(
  root: HTMLElement,
  theme: ThemeColors,
  glow: GlowIntensity = DEFAULT_GLOW,
  calendarColours: CalendarColourMode = DEFAULT_CALENDAR_COLOURS,
) {
  /**
   * How strongly the brand glow burns — stored per organization, so two orgs
   * inside one tenant can differ. An ATTRIBUTE and not a variable, because it
   * selects between token VALUES that `assets/scss/main.scss` declares; the
   * rules that light up name the token and never see this.
   */
  root.dataset.mfwGlow = glow;

  writeResolvedTheme(root, theme, calendarColours);
}

/**
 * How long a load may wait on the organization layer before the live branding
 * is painted anyway: a request that never answers must not leave the loader up
 * (or a stale cached branding on screen) for good.
 */
export const BRANDING_SETTLE_TIMEOUT_MS = 8000;

export const AppThemeProvider = ({ children }: AppThemeProviderProps) => {
  const { status: tenantStatus, tenant } = useTenantContext();

  // Read once: `main.tsx` has already painted it before the first render.
  const [cachedBranding] = React.useState(readBrandingCache);
  const [orgBranding, setOrgBranding] = React.useState<{
    logoUrl: Maybe<string>;
  } | null>(null);
  const [hasTimedOut, setHasTimedOut] = React.useState(false);
  React.useEffect(() => {
    const timer = window.setTimeout(
      () => setHasTimedOut(true),
      BRANDING_SETTLE_TIMEOUT_MS,
    );
    return () => window.clearTimeout(timer);
  }, []);
  const settleOrgBranding = React.useCallback(
    (logoUrl: Maybe<string>) =>
      setOrgBranding((current) =>
        current && current.logoUrl === logoUrl ? current : { logoUrl },
      ),
    [],
  );
  const isBrandingReady =
    hasTimedOut || (tenantStatus !== 'pending' && orgBranding !== null);

  // Rebrand: the active org's raw theme overrides (pushed by the bridge in
  // OrganizationProvider). `undefined` = tenant theme only (byte-identical to
  // the pre-rebrand behavior).
  const [orgTheme, setOrgTheme] =
    React.useState<Maybe<Record<string, string>>>(undefined);
  const [themeOptions, setThemeOptions] = React.useState<ThemeOptions>(options);

  // Single merge site: defaults ← tenant ← org (editable subset only, with
  // derived vars recomputed when the org overrides their source). Rebuilt from
  // `initialTheme` each time so switching orgs never leaves stale vars.
  //
  // Derived, not stored: this object's IDENTITY is expensive downstream.
  // `src/theme/index.tsx` memoizes `createAppTheme` on it (a full MUI theme
  // build) and `applyTheme` below repaints every `:root` var off it, while
  // `mergeOrgTheme` allocates a fresh object on every call. A state+effect
  // version minted a second, content-identical identity on mount and another
  // on every tenant refetch. Keyed to `tenant?.theme` rather than `tenant`
  // because React Query's structural sharing keeps an unchanged `theme` subtree
  // referentially stable across refetches even when the tenant object is new.
  const liveTheme = React.useMemo(
    () => mergeOrgTheme(initialTheme, tenant?.theme, orgTheme) as ThemeColors,
    [tenant?.theme, orgTheme],
  );
  // Until the live branding settles, what is on screen is the cached one —
  // painting `liveTheme` there would put the catalog defaults back up for the
  // very window the cache exists to cover.
  const theme =
    isBrandingReady || !cachedBranding
      ? liveTheme
      : (cachedBranding.theme as ThemeColors);
  /**
   * The glow intensity in force, under the same precedence the colours use:
   * catalog default ← tenant ← org. Read from the RAW layer records rather
   * than from `theme`, because `mergeOrgTheme` deliberately keeps the setting
   * keys out of it — the org layer is filtered to the editable catalog vars,
   * and the tenant layer has its settings (and legacy text keys) stripped —
   * which is exactly what keeps a non-colour from reaching `:root` as a CSS
   * variable.
   */
  const liveGlow = React.useMemo(
    () => resolveGlow(tenant?.theme, orgTheme),
    [tenant?.theme, orgTheme],
  );
  const glow =
    isBrandingReady || !cachedBranding ? liveGlow : cachedBranding.glow;
  /**
   * The calendar colour mode, from the RAW org/corp record for the same
   * reason as the glow. Org-only: the tenant record does not carry it, so with
   * no org this is Brand — the tenant's brand colours (spec §6.1).
   */
  const liveCalendarColours = React.useMemo(
    () => resolveCalendarColours(orgTheme),
    [orgTheme],
  );
  const calendarColours =
    isBrandingReady || !cachedBranding
      ? liveCalendarColours
      : cachedBranding.calendarColours;

  /**
   * Apply theme variables to global :root selector
   */
  const applyTheme = React.useCallback(
    () =>
      applyThemeToRoot(document.documentElement, theme, glow, calendarColours),
    [theme, glow, calendarColours],
  );

  React.useEffect(() => {
    applyTheme();
  }, [applyTheme]);

  // What the spinner reads to stay neutral while nothing branded is known.
  React.useEffect(() => {
    document.documentElement.dataset.mfwBranding =
      isBrandingReady || cachedBranding ? 'ready' : 'pending';
  }, [cachedBranding, isBrandingReady]);

  const liveLogoUrl = orgBranding?.logoUrl || tenant?.logoUrl || null;
  React.useEffect(() => {
    if (!isBrandingReady) return;
    writeBrandingCache({
      calendarColours: liveCalendarColours,
      glow: liveGlow,
      logoUrl: liveLogoUrl,
      theme: liveTheme,
    });
  }, [isBrandingReady, liveCalendarColours, liveGlow, liveLogoUrl, liveTheme]);

  const providedValue = React.useMemo(() => {
    return {
      applyTheme,
      hasCachedBranding: Boolean(cachedBranding),
      isBrandingReady,
      pendingLogoUrl: isBrandingReady ? undefined : cachedBranding?.logoUrl,
      setOrgTheme,
      settleOrgBranding,
      setThemeOptions,
      ...themeOptions,
      theme,
    };
  }, [
    applyTheme,
    cachedBranding,
    isBrandingReady,
    settleOrgBranding,
    themeOptions,
    theme,
  ]);

  return <Context.Provider value={providedValue}>{children}</Context.Provider>;
};

export function useAppThemeContext() {
  return Context.useContext();
}

/**
 * Resolved (merged defaults ← tenant ← org) theme var values, keyed by var
 * name — index with `themeVarName.*` from @mfw/common. Use this instead of
 * `theme.palette.*` wherever a CONCRETE color value is required and `var()`
 * references can't work: recharts/SVG presentation attributes (`fill`,
 * `stroke`) and MUI `alpha()` color math. The MUI JS theme is built once at
 * startup, so its `theme.palette` never reflects rebrand overrides; this
 * record does, reactively. Falls back to catalog defaults when rendered
 * outside the provider (e.g. router error pages).
 */
export function useThemeVars() {
  return Context.useContext().theme ?? initialTheme;
}
